import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { take } from 'rxjs';
import { VersionCheckService } from '../app/core/services/version-check.service';
import { isLocalPlaywrightHarnessPath } from './core/guards/playwright-harness.guard';
import { MainNavComponent } from './core/main-nav/main-nav.cmp';
import { normalizeTelemetryPath } from './core/services/posthog-diagnostics';
import { PosthogService } from './core/services/posthog.service';
import { ApplicationInsightsService } from './core/services/telemetry.service';
import { SpinnerComponent } from './core/spinner/spinner.cmp';
import { StmToastOutletComponent } from './shared/ui/toast/toast-outlet.component';

const REDIRECT_GUARD_KEY = 'stm_store_auth_redirects_v1';
const REDIRECT_WINDOW_MS = 90_000;
const MAX_QUICK_REDIRECTS = 3;

export type AuthRecoveryState = {
    kind: 'network' | 'auth' | 'loop';
    message: string;
};

function readRedirectGuard(): { firstTs: number; count: number } {
    try {
        const raw = sessionStorage.getItem(REDIRECT_GUARD_KEY);
        if (!raw) {
            return { firstTs: Date.now(), count: 0 };
        }
        return JSON.parse(raw) as { firstTs: number; count: number };
    } catch {
        return { firstTs: Date.now(), count: 0 };
    }
}

function shouldBlockRedirect(): boolean {
    const g = readRedirectGuard();
    const now = Date.now();
    if (now - g.firstTs > REDIRECT_WINDOW_MS) {
        return false;
    }
    return g.count >= MAX_QUICK_REDIRECTS;
}

function recordRedirectAttempt(): void {
    const now = Date.now();
    const g = readRedirectGuard();
    const resetWindow = now - g.firstTs > REDIRECT_WINDOW_MS;
    const firstTs = resetWindow ? now : g.firstTs;
    const count = resetWindow ? 1 : g.count + 1;
    sessionStorage.setItem(REDIRECT_GUARD_KEY, JSON.stringify({ firstTs, count }));
}

function clearRedirectGuard(): void {
    sessionStorage.removeItem(REDIRECT_GUARD_KEY);
}

/** Auth0 / OIDC errors that warrant a interactive re-login (not every error). */
function authErrorWantsReauth(err: unknown): boolean {
    const e = err as { error?: string; code?: string };
    const code = `${e?.error ?? ''} ${e?.code ?? ''}`.toLowerCase();
    if (!code.trim()) {
        return false;
    }
    return ['login_required', 'consent_required', 'interaction_required', 'missing_refresh_token', 'invalid_grant'].some((x) => code.includes(x));
}

function looksLikeNetworkError(err: unknown): boolean {
    if (!navigator.onLine) {
        return true;
    }
    const e = err as { message?: string; error_description?: string };
    const msg = `${e?.message ?? ''} ${e?.error_description ?? ''}`.toLowerCase();
    return msg.includes('network') || msg.includes('fetch') || msg.includes('failed to fetch') || msg.includes('timeout') || msg.includes('timed out');
}

function isReportableRefreshTokenFailure(err: unknown): boolean {
    const e = err as { error?: string; code?: string; message?: string; error_description?: string };
    const details = `${e?.error ?? ''} ${e?.code ?? ''} ${e?.message ?? ''} ${e?.error_description ?? ''}`.toLowerCase();
    return details.includes('invalid_grant') || (details.includes('refresh token') && details.includes('reuse'));
}

function getAuthErrorCode(err: unknown): string {
    const e = err as { error?: string; code?: string };
    return [e?.error, e?.code].filter(Boolean).join(' ');
}

function toError(err: unknown): Error {
    if (err instanceof Error) {
        return err;
    }

    if (typeof err === 'string') {
        return new Error(err);
    }

    try {
        return new Error(JSON.stringify(err));
    } catch {
        return new Error(String(err));
    }
}

@Component({
    selector: 'stm-app',
    templateUrl: `./app.cmp.html`,
    imports: [MainNavComponent, RouterOutlet, SpinnerComponent, StmToastOutletComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
    private readonly authService = inject(AuthService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly _router = inject(Router);
    private readonly versionCheckService = inject(VersionCheckService);
    private readonly _telemetry = inject(ApplicationInsightsService);
    private readonly _posthog = inject(PosthogService);

    public readonly authenticated = signal<boolean>(false);
    public readonly isLoading = signal<boolean>(false);
    public readonly localPlaywrightHarness = signal(isLocalPlaywrightHarnessPath(globalThis.location.pathname, globalThis.location.hostname));
    /** When set, user can recover instead of being stuck in redirect/spinner loops. */
    public readonly authRecovery = signal<AuthRecoveryState | null>(null);

    constructor() {
        if (!this.localPlaywrightHarness()) {
            this.versionCheckService.initVersionCheck('./version.json');
        }
    }

    ngOnInit(): void {
        if (this.localPlaywrightHarness()) {
            return;
        }

        this.authService.isLoading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isLoading) => this.isLoading.set(isLoading));

        this.authService.isAuthenticated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isAuthenticated) => {
            this.authenticated.set(isAuthenticated);

            if (isAuthenticated) {
                clearRedirectGuard();
                this.authRecovery.set(null);
            } else if (!this.isLoading() && shouldBlockRedirect()) {
                this.authRecovery.set({
                    kind: 'loop',
                    message: 'Sign-in was interrupted several times. Wait a moment, then try again, or refresh the page.'
                });
                this.trackAuthException(new Error('Authentication redirect loop prevented'), 'auth0.redirectLoopPrevented');
                this._telemetry.trackEvent({ name: 'authRedirectLoopPrevented' }, { route: this._router.url });
            } else if (!this.isLoading() && !shouldBlockRedirect()) {
                recordRedirectAttempt();
                this.authService
                    .loginWithRedirect({
                        appState: {
                            target: this._router.url
                        }
                    })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => {
                            /* redirect navigates away */
                        },
                        error: (error: unknown) => {
                            console.error('[AppComponent] Error initiating login redirect:', error);
                            this.authRecovery.set({
                                kind: 'auth',
                                message: 'Sign-in could not be started. Check your connection and try again.'
                            });
                            this.trackAuthException(error, 'auth0.loginRedirect');
                            this._telemetry.trackEvent({ name: 'authLoginRedirectFailed' }, { route: this._router.url });
                        }
                    });
            }
        });

        this.authService.error$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((error) => {
            if (!error) {
                return;
            }

            console.error('[AppComponent] Auth0 Error detected:', error);
            console.error('[AppComponent] Error type:', (error as { error?: string })?.error);
            console.error('[AppComponent] Error description:', (error as { error_description?: string })?.error_description);

            const errorCode = getAuthErrorCode(error);

            if (looksLikeNetworkError(error)) {
                this.authRecovery.set({
                    kind: 'network',
                    message: 'We could not reach the sign-in service. Check your network connection, then try again.'
                });
                this.trackAuthException(error, 'auth0.error$.network', errorCode);
                this._telemetry.trackEvent({ name: 'authFailureNetwork' }, { route: this._router.url, errorCode });
                return;
            }

            if (!authErrorWantsReauth(error)) {
                this._telemetry.trackException(
                    { exception: toError(error) },
                    {
                        source: 'auth0.error$',
                        errorCode,
                        phase: 'auth'
                    }
                );
                this.authRecovery.set({
                    kind: 'auth',
                    message: 'Sign-in encountered a problem. You can try again without refreshing the page.'
                });
                this._telemetry.trackEvent(
                    { name: 'authFailureNonInteractive' },
                    {
                        route: this._router.url,
                        errorCode
                    }
                );
                return;
            }

            if (isReportableRefreshTokenFailure(error)) {
                this.trackAuthException(error, 'auth0.error$.refreshToken', errorCode);
            }

            this._telemetry.trackEvent(
                { name: 'authInteractiveLoginRequired' },
                {
                    route: this._router.url,
                    errorCode
                }
            );

            if (shouldBlockRedirect()) {
                this.authRecovery.set({
                    kind: 'loop',
                    message: 'Sign-in was interrupted several times. Wait a moment, then try again, or refresh the page.'
                });
                this.trackAuthException(new Error('Authentication redirect loop prevented'), 'auth0.redirectLoopPrevented', errorCode);
                this._telemetry.trackEvent({ name: 'authRedirectLoopPrevented' }, { route: this._router.url, errorCode });
                return;
            }

            recordRedirectAttempt();
            this.authService
                .loginWithRedirect({
                    appState: {
                        target: this._router.url
                    }
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: () => {
                        /* redirect navigates away */
                    },
                    error: (redirectError: unknown) => {
                        console.error('[AppComponent] Error during login redirect after auth error:', redirectError);
                        this.authRecovery.set({
                            kind: 'auth',
                            message: 'Could not restart sign-in. Try again in a moment.'
                        });
                        this.trackAuthException(redirectError, 'auth0.reauthRedirect', errorCode);
                        this._telemetry.trackEvent({ name: 'authReauthRedirectFailed' }, { route: this._router.url, errorCode });
                    }
                });
        });
    }

    retryAuth(): void {
        clearRedirectGuard();
        this.authRecovery.set(null);
        this.authService
            .loginWithRedirect({
                appState: {
                    target: this._router.url
                }
            })
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    /* redirect navigates away */
                },
                error: (e: unknown) => {
                    console.error('[AppComponent] retryAuth loginWithRedirect failed:', e);
                    this.authRecovery.set({
                        kind: 'auth',
                        message: 'Could not start sign-in. Check your connection and try again.'
                    });
                    this.trackAuthException(e, 'auth0.retryRedirect');
                    this._telemetry.trackEvent({ name: 'authLoginRedirectFailed' }, { route: this._router.url, retry: 'true' });
                }
            });
    }

    private trackAuthException(error: unknown, source: string, errorCode = ''): void {
        const route = normalizeTelemetryPath(this._router.url) ?? 'unknown';
        this._telemetry.trackException(
            { exception: toError(error) },
            {
                source,
                errorCode,
                phase: 'auth',
                route
            }
        );
    }

    reloadApp(): void {
        window.location.reload();
    }
}
